import { useState, useEffect } from 'react';
import { useUserPreferences, useStoreActions, useSettingsStore } from '@/store/useStore';
import { Calendar, Clock, MapPin, FileText, MessageSquare, User, Search, Bell, Bus, Bot, Home, ClipboardList, ChevronRight, LayoutGrid, PackageSearch } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { DataProcessor } from '@/utils/dataProcessor';
import { findUserProgram } from '@/utils/programMatching';
import { getOrderedDays } from '@/utils/dayUtils';
import { applyTimetableProfile } from '@/utils/timetableProfiles';
import Logo from '@/components/Logo';
import { useDashboard } from '@/contexts/DashboardContext';
import SemesterMigrationNotice from '@/components/SemesterMigrationNotice';
import GraduationNotice from '@/components/GraduationNotice';
import { useGraduationNotice, useHasGraduated } from '@/hooks/useSemesterCompletion';

// ─── Types ────────────────────────────────────────────────────────────────────
interface TimetableProfileControl {
  officialStartDate?: string;
  officialEndDate?: string;
  predictedStartDate?: string;
  predictedEndDate?: string;
}
interface TimetableProfileControlFile {
  profiles?: Record<string, TimetableProfileControl>;
}

// ─── Sidebar nav config ───────────────────────────────────────────────────────
const MAIN_NAV = [
  { path: '/dashboard/home',       Icon: Home,          label: 'Home'       },
  { path: '/dashboard/personal',   Icon: Calendar,      label: 'Timetable'  },
  { path: '/dashboard/browse',     Icon: LayoutGrid,    label: 'Browse'     },
  { path: '/dashboard/rooms',      Icon: MapPin,        label: 'Campus Map' },
  { path: '/dashboard/bus',        Icon: Bus,           label: 'Bus Routes' },
  { path: '/dashboard/lost-found', Icon: PackageSearch, label: 'Lost & Found' },
];
const ACADEMIC_NAV = [
  { path: '/dashboard/calendar',   Icon: Calendar,      label: 'Academics'  },
  { path: '/dashboard/exams',      Icon: FileText,      label: 'Exams'      },
  { path: '/dashboard/owlet',      Icon: Bot,           label: 'Owlet AI'   },
  { path: '/dashboard/feedback',   Icon: MessageSquare, label: 'Feedback'   },
  { path: '/dashboard/attendance', Icon: ClipboardList, label: 'Attendance' },
];
const TOP_NAV = [
  { path: '/dashboard/home',     label: 'Home'      },
  { path: '/dashboard/personal', label: 'Timetable' },
  { path: '/dashboard/browse',   label: 'Browse'    },
  { path: '/dashboard/rooms',    label: 'Rooms'     },
  { path: '/dashboard/exams',    label: 'Exams'     },
];

// ─── Primitives ───────────────────────────────────────────────────────────────

function SidebarLink({ path, Icon, label }: { path: string; Icon: any; label: string }) {
  const { pathname } = useLocation();
  const active = pathname === path;
  const [hov, setHov] = useState(false);
  return (
    <Link
      to={path}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 9,
        padding: '9px 10px', borderRadius: 9, textDecoration: 'none',
        fontSize: 12, transition: '.15s',
        background: active ? 'rgba(20,184,166,.1)' : hov ? 'rgba(255,255,255,.04)' : 'transparent',
        border: `1px solid ${active ? 'rgba(20,184,166,.18)' : 'transparent'}`,
        color: active ? '#14B8A6' : hov ? 'rgba(255,255,255,.75)' : 'rgba(255,255,255,.4)',
      }}
    >
      <Icon size={15} aria-hidden />
      <span>{label}</span>
    </Link>
  );
}

function NavIconBtn({ children, onClick, badge }: { children: React.ReactNode; onClick?: () => void; badge?: number }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: 32, height: 32, borderRadius: 9, flexShrink: 0,
        background: hov ? 'rgba(255,255,255,.08)' : 'rgba(255,255,255,.05)',
        border: '1px solid rgba(255,255,255,.07)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: hov ? '#fff' : 'rgba(255,255,255,.45)',
        cursor: 'pointer', transition: '.2s', position: 'relative',
      }}
    >
      {children}
      {badge && badge > 0 ? (
        <span style={{
          position: 'absolute', top: -3, right: -3,
          width: 14, height: 14, borderRadius: '50%',
          background: '#EF4444', border: '2px solid #0A0A0A',
          fontSize: 8, display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontWeight: 700, color: '#fff',
        }}>{badge}</span>
      ) : null}
    </button>
  );
}

function SectionLabel({ text }: { text: string }) {
  return (
    <div style={{
      fontFamily: "'JetBrains Mono',monospace",
      fontSize: 8, letterSpacing: '.18em',
      color: 'rgba(255,255,255,.2)',
      padding: '10px 10px 6px',
    }}>{text}</div>
  );
}

function SectionHeader({ title, meta }: { title: string; meta?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
      <span style={{ fontFamily: "'Instrument Serif',serif", fontSize: 20, fontWeight: 400, letterSpacing: '-.01em' }}>
        {title}
      </span>
      {meta && (
        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '.14em', color: 'rgba(255,255,255,.25)' }}>
          {meta}
        </span>
      )}
    </div>
  );
}

function EmptyState({ icon: Icon, text }: { icon: any; text: string }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.07)',
      borderRadius: 12, padding: '28px 20px', textAlign: 'center',
    }}>
      <Icon size={22} color="rgba(255,255,255,.2)" style={{ margin: '0 auto 8px', display: 'block' }} aria-hidden />
      <p style={{ fontSize: 12, color: 'rgba(255,255,255,.3)', fontWeight: 300 }}>{text}</p>
    </div>
  );
}

// ─── Status badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { bg: string; border: string; color: string; label: string }> = {
    ongoing:   { bg: 'rgba(20,184,166,.1)',   border: 'rgba(20,184,166,.25)',  color: '#14B8A6',              label: 'LIVE' },
    upcoming:  { bg: 'rgba(96,165,250,.08)',  border: 'rgba(96,165,250,.2)',   color: '#60A5FA',              label: 'NEXT' },
    completed: { bg: 'rgba(255,255,255,.04)', border: 'rgba(255,255,255,.08)', color: 'rgba(255,255,255,.22)', label: 'DONE' },
    cancelled: { bg: 'rgba(239,68,68,.08)',   border: 'rgba(239,68,68,.18)',   color: 'rgba(239,68,68,.65)',  label: 'OFF'  },
  };
  const s = map[status] ?? map.upcoming;
  return (
    <span style={{
      fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '.07em',
      padding: '2px 7px', borderRadius: 4, fontWeight: 500, whiteSpace: 'nowrap',
      background: s.bg, border: `1px solid ${s.border}`, color: s.color,
    }}>{s.label}</span>
  );
}

// ─── Timeline card ────────────────────────────────────────────────────────────
function TimelineCard({ cls }: { cls: any }) {
  const [hov, setHov] = useState(false);
  const parts      = cls.time.split('-');
  const startRaw   = parts[0]?.trim() ?? '';
  const endRaw     = parts[1]?.trim() ?? '';
  const timeH      = startRaw.replace(/(AM|PM)/i, '').trim();
  const timeP      = startRaw.match(/(AM|PM)/i)?.[0] ?? '';

  const dotColor: Record<string, string> = {
    ongoing:   '#14B8A6',
    upcoming:  'rgba(96,165,250,.55)',
    completed: 'rgba(255,255,255,.15)',
    cancelled: 'rgba(239,68,68,.4)',
  };
  const dotGlow = cls.status === 'ongoing' ? '0 0 8px rgba(20,184,166,.55)' : 'none';

  return (
    <div style={{ display: 'flex', alignItems: 'stretch' }}>
      {/* Time col */}
      <div style={{ width: 46, flexShrink: 0, padding: '12px 8px 12px 0', textAlign: 'right' }}>
        <div style={{
          fontFamily: "'JetBrains Mono',monospace", fontSize: 11, lineHeight: 1,
          color: cls.status === 'ongoing' ? '#14B8A6' : 'rgba(255,255,255,.5)',
        }}>{timeH}</div>
        <div style={{
          fontFamily: "'JetBrains Mono',monospace", fontSize: 8,
          color: 'rgba(255,255,255,.2)', marginTop: 1,
        }}>{timeP}</div>
      </div>

      {/* Dot */}
      <div style={{
        width: 14, flexShrink: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        zIndex: 1, padding: '14px 0',
      }}>
        <div style={{
          width: 8, height: 8, borderRadius: '50%', flexShrink: 0, marginTop: 2,
          background: dotColor[cls.status] ?? dotColor.upcoming,
          boxShadow: dotGlow,
        }} />
      </div>

      {/* Card */}
      <div
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          flex: 1, margin: '8px 0 8px 14px',
          background: cls.status === 'ongoing' ? 'rgba(20,184,166,.07)' : hov ? 'rgba(255,255,255,.04)' : 'rgba(255,255,255,.03)',
          border: `1px solid ${cls.status === 'ongoing' ? 'rgba(20,184,166,.18)' : hov ? 'rgba(255,255,255,.1)' : 'rgba(255,255,255,.06)'}`,
          borderRadius: 10, padding: '10px 14px',
          opacity: cls.status === 'completed' ? .38 : cls.status === 'cancelled' ? .32 : 1,
          transition: 'opacity .2s, background .2s, border-color .2s',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 5 }}>
          <span style={{
            fontSize: 12, fontWeight: 500, lineHeight: 1.2, flex: 1,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            textDecoration: cls.isCancelled ? 'line-through' : 'none',
          }}>{cls.course}</span>
          <StatusBadge status={cls.status} />
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          {cls.instructor && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'rgba(255,255,255,.3)', fontWeight: 300 }}>
              <User size={10} color="rgba(20,184,166,.55)" aria-hidden /> {cls.instructor}
            </div>
          )}
          {cls.room && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'rgba(255,255,255,.3)', fontWeight: 300 }}>
              <MapPin size={10} color="rgba(20,184,166,.55)" aria-hidden /> {cls.room}
            </div>
          )}
          {endRaw && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'rgba(255,255,255,.2)', fontWeight: 300, marginLeft: 'auto' }}>
              <Clock size={10} color="rgba(255,255,255,.2)" aria-hidden /> until {endRaw}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Quick access button ──────────────────────────────────────────────────────
function QuickAccessBtn({ Icon, label, href }: { Icon: any; label: string; href: string }) {
  const [hov, setHov] = useState(false);
  return (
    <Link to={href} style={{ textDecoration: 'none' }}>
      <div
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '11px 14px',
          background: hov ? 'rgba(20,184,166,.07)' : 'rgba(255,255,255,.03)',
          border: `1px solid ${hov ? 'rgba(20,184,166,.2)' : 'rgba(255,255,255,.06)'}`,
          borderRadius: 10, cursor: 'pointer', transition: '.2s',
        }}
      >
        <div style={{
          width: 28, height: 28, borderRadius: 8, flexShrink: 0,
          background: 'rgba(20,184,166,.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#14B8A6',
          transform: hov ? 'scale(1.08)' : 'scale(1)', transition: 'transform .2s',
        }}>
          <Icon size={13} aria-hidden />
        </div>
        <span style={{ fontSize: 12, color: hov ? 'rgba(255,255,255,.7)' : 'rgba(255,255,255,.45)' }}>
          {label}
        </span>
        <ChevronRight size={12} color="rgba(255,255,255,.15)" style={{ marginLeft: 'auto' }} aria-hidden />
      </div>
    </Link>
  );
}

// ─── Main page (desktop only — hidden on mobile via className) ────────────────
export default function HomePageDesktop() {
  const dashboard      = useDashboard();
  const userPreferences = useUserPreferences();
  const { initializeData } = useStoreActions();
  const { preferences }    = useSettingsStore();
  const graduation         = useGraduationNotice(userPreferences);
  const { graduated: hasGraduated } = useHasGraduated(userPreferences);

  const [currentDateTime,    setCurrentDateTime]    = useState(new Date());
  const [isLoading,          setIsLoading]          = useState(true);
  const [todayClasses,       setTodayClasses]       = useState<any[]>([]);
  const [showMigrationNotice,setShowMigrationNotice]= useState(false);
  const [fullWeekTimetable,  setFullWeekTimetable]  = useState<Record<string, any[]>>({});
  const [selectedDayView,    setSelectedDayView]    = useState(new Date().toLocaleDateString('en-US', { weekday: 'long' }));
  const [ramadanDateLabel,   setRamadanDateLabel]   = useState('');
  const [ramadanWindow,      setRamadanWindow]      = useState<{ start?: Date; end?: Date }>({});

  useEffect(() => {
    const t = setInterval(() => setCurrentDateTime(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const pakistanTime = new Date(currentDateTime.toLocaleString('en-US', { timeZone: 'Asia/Karachi' }));
  const selectedDay  = pakistanTime.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'Asia/Karachi' });
  const currentTime  = pakistanTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true,  timeZone: 'Asia/Karachi' });
  const displayTime  = pakistanTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Karachi' });

  const parseTimeToMinutes = (t: string) => {
    const m = t.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!m) return 0;
    let h = parseInt(m[1]); const min = parseInt(m[2]); const p = m[3].toUpperCase();
    if (p === 'PM' && h !== 12) h += 12;
    if (p === 'AM' && h === 12) h = 0;
    return h * 60 + min;
  };
  const currentMinutes = parseTimeToMinutes(currentTime);

  const getOrdinal = (n: number) => {
    const v = n % 100;
    if (v >= 11 && v <= 13) return `${n}th`;
    switch (n % 10) { case 1: return `${n}st`; case 2: return `${n}nd`; case 3: return `${n}rd`; default: return `${n}th`; }
  };

  const numberToRoman = (num: number | string) => {
    const n = typeof num === 'string' ? parseInt(num) : num;
    return (['','I','II','III','IV','V','VI','VII','VIII','IX','X'][n]) || String(num);
  };

  // Ramadan window
  useEffect(() => {
    (async () => {
      try {
        const res     = await fetch('/timetable-profile-control.json', { cache: 'no-store' });
        if (!res.ok) return;
        const payload = await res.json() as TimetableProfileControlFile;
        const profile = payload.profiles?.IMS_Bachelors_Ramadan_2026;
        if (!profile) return;
        const s = profile.officialStartDate || profile.predictedStartDate || '2026-02-19';
        const e = profile.officialEndDate   || profile.predictedEndDate   || '2026-03-21';
        setRamadanWindow({ start: new Date(`${s}T00:00:00`), end: new Date(`${e}T00:00:00`) });
      } catch {}
    })();
  }, []);

  useEffect(() => {
    const today = new Date(pakistanTime); today.setHours(0,0,0,0);
    if (!ramadanWindow.start) { setRamadanDateLabel(''); return; }
    const start = new Date(ramadanWindow.start); start.setHours(0,0,0,0);
    const end   = ramadanWindow.end ? new Date(ramadanWindow.end) : undefined;
    if (end) end.setHours(0,0,0,0);
    if (!(today >= start && (!end || today <= end))) { setRamadanDateLabel(''); return; }
    const day = Math.floor((today.getTime() - start.getTime()) / 86400000) + 1;
    setRamadanDateLabel(`Ramadan ${getOrdinal(Math.max(1, day))}, ${today.getFullYear()}`);
  }, [currentDateTime, ramadanWindow.start, ramadanWindow.end]);

  // Next class
  const getNextUpcomingClass = () => {
    const updated  = todayClasses.map(cls => {
      let status = 'upcoming';
      if (currentMinutes >= cls.startMinutes && currentMinutes < cls.endMinutes) status = 'ongoing';
      else if (currentMinutes >= cls.endMinutes) status = 'completed';
      return { ...cls, status };
    });
    const upcoming = updated.filter(c => c.status === 'upcoming');
    if (upcoming.length > 0) {
      const next = upcoming[0];
      const diff = next.startMinutes - currentMinutes;
      const h = Math.floor(diff / 60), m = diff % 60;
      return { ...next, timeUntil: diff <= 0 ? 'Now' : h > 0 ? `${h}h ${m}m` : `${m} min`, isToday: true };
    }
    if (userPreferences) {
      const dp          = DataProcessor.getInstance();
      const userProgram = findUserProgram(dp.getAllPrograms(), userPreferences);
      if (userProgram) {
        const { schedule } = applyTimetableProfile(userProgram.schedule, { institute: 'Institute of Management Sciences', department: userProgram.department, program: userProgram.program, referenceDate: pakistanTime });
        const days = getOrderedDays(preferences.weekStartsOn, true);
        const idx  = days.indexOf(selectedDay);
        for (let i = 1; i <= 7; i++) {
          const nextDay = days[(idx + i) % 7];
          if (schedule[nextDay]?.length > 0) {
            const fc = schedule[nextDay][0];
            return { time: fc.time, course: fc.course_name || fc.course, room: fc.location, instructor: fc.instructor || fc.teacher, timeUntil: i === 1 ? 'Tomorrow' : `${i} days`, day: nextDay, isToday: false };
          }
        }
      }
    }
    return null;
  };
  const nextClass = getNextUpcomingClass();

  // Load data
  useEffect(() => {
    const load = async () => {
      await initializeData();
      if (userPreferences) {
        const dp          = DataProcessor.getInstance();
        const userProgram = findUserProgram(dp.getAllPrograms(), userPreferences);
        const effSched    = userProgram
          ? applyTimetableProfile(userProgram.schedule, { institute: 'Institute of Management Sciences', department: userProgram.department, program: userProgram.program, referenceDate: pakistanTime }).schedule
          : null;

        if (userProgram && effSched?.[selectedDay]) {
          const cancelled = new Set(JSON.parse(localStorage.getItem('cancelledClasses') || '[]').filter((id: string) => id.startsWith(`${userProgram.id}-`)));
          const classes   = effSched[selectedDay]
            .map((course: any, i: number) => {
              const [st, et] = course.time.split('-').map((t: string) => t.trim());
              const startMinutes = parseTimeToMinutes(st);
              const endMinutes   = parseTimeToMinutes(et);
              const classId      = `${userProgram.id}-${selectedDay}-${i}`;
              const isCancelled  = cancelled.has(classId);
              let status = 'upcoming';
              if (isCancelled) status = 'cancelled';
              else if (currentMinutes >= startMinutes && currentMinutes < endMinutes) status = 'ongoing';
              else if (currentMinutes >= endMinutes) status = 'completed';
              return { id: classId, time: course.time, startMinutes, endMinutes, course: course.course_name || course.course, room: course.location, instructor: course.instructor || course.teacher, status, isCancelled };
            })
            .sort((a: any, b: any) => a.startMinutes - b.startMinutes);
          setTodayClasses(classes);
          setShowMigrationNotice(false);

          if (preferences.showFullTimetableOnDashboard) {
            const days = getOrderedDays(preferences.weekStartsOn, preferences.showWeekends);
            const wt: Record<string, any[]> = {};
            days.forEach((day: string) => {
              wt[day] = (effSched[day] || [])
                .map((c: any) => ({ time: c.time, course: c.course_name || c.course, room: c.location, instructor: c.instructor || c.teacher }))
                .sort((a: any, b: any) => parseTimeToMinutes(a.time.split('-')[0].trim()) - parseTimeToMinutes(b.time.split('-')[0].trim()));
            });
            setFullWeekTimetable(wt);
          }
        } else if (!userProgram) {
          setShowMigrationNotice(true);
        }
      }
      setIsLoading(false);
    };
    load();
  }, [userPreferences, selectedDay, initializeData, currentMinutes, preferences.showFullTimetableOnDashboard, preferences.weekStartsOn, preferences.showWeekends]);

  if (isLoading) {
    return (
      <div className="hidden lg:flex" style={{ background: '#0A0A0A', minHeight: '100vh', alignItems: 'stretch' }}>
        {/* Sidebar skeleton */}
        <div style={{ width: 220, borderRight: '1px solid rgba(255,255,255,.07)', padding: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[40,32,32,32,32,32,32].map((h,i) => (
            <div key={i} style={{ height: h, background: 'rgba(255,255,255,.04)', borderRadius: 8, animation: 'dbpulse 1.4s ease-in-out infinite', animationDelay: `${i*0.08}s` }} />
          ))}
        </div>
        {/* Content skeleton */}
        <div style={{ flex: 1, padding: 28, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[80,140,200].map((h,i) => (
            <div key={i} style={{ height: h, background: 'rgba(255,255,255,.04)', borderRadius: 12, animation: 'dbpulse 1.4s ease-in-out infinite', animationDelay: `${i*0.1}s` }} />
          ))}
        </div>
        <style>{`@keyframes dbpulse{0%,100%{opacity:.3}50%{opacity:.7}}`}</style>
      </div>
    );
  }

  // Checked before the migration notice — see the note in HomePage.
  if (graduation.show && graduation.semesterLabel) {
    return (
      <GraduationNotice
        userName={userPreferences?.name}
        semesterLabel={graduation.semesterLabel}
        onDismiss={graduation.dismiss}
      />
    );
  }

  if (showMigrationNotice && !hasGraduated) {
    return <SemesterMigrationNotice userName={userPreferences?.name} />;
  }

  const doneCount     = todayClasses.filter(c => c.status === 'completed').length;
  const upcomingCount = todayClasses.filter(c => c.status === 'upcoming' || c.status === 'ongoing').length;
  const unreadCount   = dashboard ? dashboard.generateNotifications().filter((n: any) => !dashboard.readNotifications.has(n.id)).length : 0;

  return (
    <div
      className="hidden lg:flex"
      style={{ background: '#0A0A0A', fontFamily: "'Outfit',sans-serif", color: '#fff', minHeight: '100vh', flexDirection: 'column' }}
    >
      {/* ── Top bar ─────────────────────────────────────── */}
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 28px',
        borderBottom: '1px solid rgba(255,255,255,.06)',
        background: '#0A0A0A', position: 'sticky', top: 0, zIndex: 50,
        flexShrink: 0,
      }}>
        <Logo size="sm" clickable navigateTo="/dashboard/home" />

        {/* Desktop nav links */}
        <div style={{ display: 'flex', gap: 24 }}>
          {TOP_NAV.map(({ path, label }) => (
            <TopNavLink key={path} path={path} label={label} />
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <NavIconBtn onClick={() => dashboard?.setShowSearch(true)}>
            <Search size={14} aria-hidden />
          </NavIconBtn>
          <NavIconBtn
            badge={unreadCount}
            onClick={() => { if (dashboard) { dashboard.setShowNotifications(!dashboard.showNotifications); if (!dashboard.showNotifications) setTimeout(() => dashboard.markNotificationsAsRead(), 100); } }}
          >
            <Bell size={14} aria-hidden />
          </NavIconBtn>
          <button
            onClick={() => dashboard?.setShowUserMenu(!dashboard?.showUserMenu)}
            style={{
              width: 32, height: 32, borderRadius: 9, flexShrink: 0,
              background: 'rgba(20,184,166,.15)', border: '1px solid rgba(20,184,166,.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#14B8A6', fontFamily: "'JetBrains Mono',monospace",
              fontSize: 10, fontWeight: 500, cursor: 'pointer',
            }}
          >
            {userPreferences?.name?.charAt(0).toUpperCase() ?? 'U'}
          </button>
        </div>
      </header>

      {/* ── Body: sidebar + content ──────────────────────── */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* ── Sidebar ──────────────────────────────────── */}
        <aside style={{
          width: 220, flexShrink: 0,
          borderRight: '1px solid rgba(255,255,255,.06)',
          padding: '20px 16px',
          display: 'flex', flexDirection: 'column',
          background: '#0A0A0A',
          overflowY: 'auto',
        }}>
          <SectionLabel text="MAIN" />
          {MAIN_NAV.map(item => <SidebarLink key={item.path} {...item} />)}

          <SectionLabel text="ACADEMICS" />
          {ACADEMIC_NAV.map(item => <SidebarLink key={item.path} {...item} />)}

          <div style={{ flex: 1 }} />

          {/* Profile chip */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '12px 10px',
            borderTop: '1px solid rgba(255,255,255,.06)',
            marginTop: 8,
          }}>
            <div style={{
              width: 30, height: 30, borderRadius: 9, flexShrink: 0,
              background: 'rgba(20,184,166,.15)', border: '1px solid rgba(20,184,166,.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#14B8A6', fontFamily: "'JetBrains Mono',monospace",
              fontSize: 10, fontWeight: 500,
            }}>
              {userPreferences?.name?.charAt(0).toUpperCase() ?? 'U'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {userPreferences?.name || 'Student'}
              </div>
              <div style={{
                fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '.06em',
                color: 'rgba(255,255,255,.25)', marginTop: 1,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {[userPreferences?.program, userPreferences?.semester ? `SEM ${numberToRoman(userPreferences.semester)}` : null, userPreferences?.group ? `GRP ${userPreferences.group}` : null].filter(Boolean).join(' · ')}
              </div>
            </div>
          </div>
        </aside>

        {/* ── Main content ─────────────────────────────── */}
        <main style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 22 }}>

          {/* ── Top row: greeting + next class ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.1fr', gap: 14 }}>

            {/* Greeting */}
            <div style={{
              background: 'linear-gradient(135deg,rgba(20,184,166,.11) 0%,rgba(6,182,212,.06) 100%)',
              border: '1px solid rgba(20,184,166,.16)',
              borderRadius: 14, padding: '20px 22px',
              position: 'relative', overflow: 'hidden',
              display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
              minHeight: 140,
            }}>
              <div style={{ position: 'absolute', top: -40, right: -40, width: 160, height: 160, background: 'radial-gradient(circle,rgba(20,184,166,.18) 0%,transparent 65%)', pointerEvents: 'none' }} />

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
                <div>
                  {ramadanDateLabel && (
                    <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '.16em', color: 'rgba(20,184,166,.65)', marginBottom: 6 }}>
                      {ramadanDateLabel.toUpperCase()}
                    </div>
                  )}
                  <div style={{ fontFamily: "'Instrument Serif',serif", fontSize: 26, fontWeight: 400, letterSpacing: '-.02em', lineHeight: 1 }}>
                    {selectedDay}
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,.4)', fontWeight: 300, marginTop: 4 }}>
                    {pakistanTime.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'Asia/Karachi' })}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: "'Instrument Serif',serif", fontSize: 30, fontWeight: 400, letterSpacing: '-.02em', lineHeight: 1 }}>
                    {displayTime}
                  </div>
                  <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '.1em', color: 'rgba(255,255,255,.25)', marginTop: 3 }}>
                    PKT
                  </div>
                </div>
              </div>

              {/* Mini stats */}
              <div style={{
                display: 'flex', borderTop: '1px solid rgba(255,255,255,.07)',
                paddingTop: 14, position: 'relative', zIndex: 1,
              }}>
                {[
                  { num: String(todayClasses.length), lbl: 'TODAY'  },
                  { num: String(doneCount),           lbl: 'DONE'   },
                  { num: String(upcomingCount),        lbl: 'AHEAD'  },
                ].map(({ num, lbl }, i, arr) => (
                  <div key={lbl} style={{ display: 'flex', flex: 1, alignItems: 'center' }}>
                    <div style={{ flex: 1, textAlign: 'center' }}>
                      <div style={{ fontFamily: "'Instrument Serif',serif", fontSize: 22, color: '#14B8A6', lineHeight: 1 }}>{num}</div>
                      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '.1em', color: 'rgba(255,255,255,.25)', marginTop: 3 }}>{lbl}</div>
                    </div>
                    {i < arr.length - 1 && <div style={{ width: 1, height: 28, background: 'rgba(255,255,255,.08)' }} />}
                  </div>
                ))}
              </div>
            </div>

            {/* Next class */}
            {nextClass ? (
              <div style={{
                background: 'rgba(255,255,255,.03)',
                border: '1px solid rgba(255,255,255,.07)',
                borderRadius: 14, padding: '20px 22px',
                position: 'relative', overflow: 'hidden',
              }}>
                <div style={{ position: 'absolute', bottom: -30, right: -20, width: 120, height: 120, background: 'radial-gradient(circle,rgba(96,165,250,.08) 0%,transparent 65%)', pointerEvents: 'none' }} />
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 8, letterSpacing: '.16em', color: 'rgba(255,255,255,.3)' }}>UP NEXT</span>
                    <span style={{
                      background: 'rgba(20,184,166,.12)', border: '1px solid rgba(20,184,166,.22)',
                      borderRadius: 20, padding: '3px 10px',
                      fontFamily: "'JetBrains Mono',monospace", fontSize: 9, color: '#14B8A6', letterSpacing: '.07em',
                    }}>In {nextClass.timeUntil}</span>
                  </div>
                  <div style={{ fontFamily: "'Instrument Serif',serif", fontSize: 20, fontWeight: 400, letterSpacing: '-.02em', lineHeight: 1.1, marginBottom: 12 }}>
                    {nextClass.course}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {[
                      { Icon: User,   text: nextClass.instructor },
                      { Icon: Clock,  text: nextClass.time       },
                      { Icon: MapPin, text: nextClass.room       },
                    ].filter(m => m.text).map(({ Icon, text }) => (
                      <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'rgba(255,255,255,.45)', fontWeight: 300 }}>
                        <Icon size={13} color="rgba(20,184,166,.65)" aria-hidden /> {text}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{
                background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.07)',
                borderRadius: 14, padding: '20px 22px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <div style={{ textAlign: 'center' }}>
                  <Calendar size={24} color="rgba(255,255,255,.2)" style={{ margin: '0 auto 8px' }} aria-hidden />
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,.3)', fontWeight: 300 }}>No more classes today</p>
                </div>
              </div>
            )}
          </div>

          {/* ── Mid row: timeline + quick access ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: 14, alignItems: 'start' }}>

            {/* Timeline schedule */}
            <div>
              <SectionHeader
                title="Today's Schedule"
                meta={todayClasses.length > 0 ? `${selectedDay.substring(0,3).toUpperCase()} · ${todayClasses.length} CLASSES` : undefined}
              />

              {todayClasses.length === 0 ? (
                <EmptyState icon={Calendar} text="No classes scheduled for today" />
              ) : (
                <div style={{ position: 'relative' }}>
                  {/* Spine */}
                  <div style={{
                    position: 'absolute', left: 52, top: 14, bottom: 14,
                    width: 1, background: 'rgba(255,255,255,.06)',
                  }} />
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {todayClasses.map(cls => <TimelineCard key={cls.id} cls={cls} />)}
                  </div>
                </div>
              )}
            </div>

            {/* Quick access */}
            <div>
              <SectionHeader title="Quick Access" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <QuickAccessBtn Icon={Bus}          label="Bus Routes" href="/dashboard/bus"      />
                <QuickAccessBtn Icon={Calendar}     label="Calendar"   href="/dashboard/calendar" />
                <QuickAccessBtn Icon={FileText}     label="Exams"      href="/dashboard/exams"    />
                <QuickAccessBtn Icon={Bot}          label="Owlet AI"   href="/dashboard/owlet"    />
                <QuickAccessBtn Icon={MessageSquare}label="Feedback"   href="/dashboard/feedback" />
                <QuickAccessBtn Icon={PackageSearch} label="Lost & Found" href="/dashboard/lost-found" />
              </div>
            </div>
          </div>

          {/* ── Full week timetable (optional) ── */}
          {preferences.showFullTimetableOnDashboard && Object.keys(fullWeekTimetable).length > 0 && (
            <div>
              <SectionHeader title="Full Week" meta="SCHEDULE" />
              <div style={{ display: 'flex', gap: 8, marginBottom: 16, overflowX: 'auto', paddingBottom: 4 }}>
                {Object.keys(fullWeekTimetable).map(day => (
                  <button
                    key={day}
                    onClick={() => setSelectedDayView(day)}
                    style={{
                      padding: '6px 14px', borderRadius: 8, cursor: 'pointer', border: 'none',
                      fontFamily: "'JetBrains Mono',monospace", fontSize: 10, letterSpacing: '.08em',
                      whiteSpace: 'nowrap', transition: '.2s',
                      background: selectedDayView === day ? 'rgba(20,184,166,.15)' : 'rgba(255,255,255,.04)',
                      color:      selectedDayView === day ? '#14B8A6'             : 'rgba(255,255,255,.4)',
                      borderBottom: selectedDayView === day ? '1px solid rgba(20,184,166,.35)' : '1px solid transparent',
                    }}
                  >{day.substring(0,3).toUpperCase()}</button>
                ))}
              </div>
              {fullWeekTimetable[selectedDayView]?.length === 0 ? (
                <EmptyState icon={Calendar} text={`No classes on ${selectedDayView}`} />
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
                  {fullWeekTimetable[selectedDayView].map((cls: any, i: number) => (
                    <div key={i} style={{
                      background: 'rgba(255,255,255,.03)', border: '1px solid rgba(255,255,255,.07)',
                      borderLeft: '2px solid #14B8A6', borderRadius: 10, padding: '12px 14px',
                    }}>
                      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>{cls.course}</div>
                      {[{ Icon: Clock, text: cls.time }, { Icon: MapPin, text: cls.room }, { Icon: User, text: cls.instructor }].filter(m => m.text).map(({ Icon, text }) => (
                        <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'rgba(255,255,255,.35)', fontWeight: 300, marginBottom: 4 }}>
                          <Icon size={11} color="rgba(20,184,166,.55)" aria-hidden /> {text}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </main>
      </div>

      <style>{`@keyframes dbpulse{0%,100%{opacity:.3}50%{opacity:.7}}`}</style>
    </div>
  );
}

// ─── Top nav link ─────────────────────────────────────────────────────────────
function TopNavLink({ path, label }: { path: string; label: string }) {
  const { pathname } = useLocation();
  const active       = pathname === path;
  const [hov, setHov]= useState(false);
  return (
    <Link
      to={path}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        fontSize: 12, textDecoration: 'none', transition: '.2s',
        color: active ? '#fff' : hov ? 'rgba(255,255,255,.65)' : 'rgba(255,255,255,.35)',
        paddingBottom: 2,
        borderBottom: active ? '1px solid rgba(20,184,166,.5)' : '1px solid transparent',
      }}
    >{label}</Link>
  );
}
